import { Component, ElementRef, Input, ViewChild, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, NgZone, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MODE_DRAWS } from './engine/registry';
import { resolvePreset } from './presets';
import { getResolvedDark, getReducedMotion } from './theme';
import type { OrbState, OrbTheme, OrbSize } from './types';

@Component({
  selector: 'app-thinking-orb',
  standalone: true,
  template: `<canvas #orbCanvas role="img" [attr.aria-label]="ariaLabel || defaultLabel" [style.width.px]="size" [style.height.px]="size" style="display: block; image-rendering: pixelated;"></canvas>`
})
export class ThinkingOrbComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() state: OrbState = 'working';
  @Input() size: number = 64;
  @Input() theme: OrbTheme = 'auto';
  @Input() speed: number = 1;
  @Input() paused: boolean = false;
  @Input() ariaLabel: string = '';

  @ViewChild('orbCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  private rafId: number = 0;
  private running: boolean = false;
  private io: IntersectionObserver | null = null;
  private visible: boolean = true;
  private ctx: CanvasRenderingContext2D | null = null;
  private dpr: number = 1;

  private readonly LABELS: Record<string, string> = {
    working: 'Procesando…',
    searching: 'Buscando…',
    solving: 'Resolviendo…',
    listening: 'Escuchando…',
    composing: 'Componiendo…',
    shaping: 'Formando…'
  };

  constructor(
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  get defaultLabel(): string {
    return this.LABELS[this.state];
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const canvas = this.canvasRef.nativeElement;
    // Cap maximum rendering resolution to 24x24 for a retro, low-res pixel art look.
    // If the size is smaller than 24, it renders 1:1, which is naturally pixelated.
    this.dpr = Math.min(1, 24 / this.size);
    canvas.width = Math.round(this.size * this.dpr);
    canvas.height = Math.round(this.size * this.dpr);
    this.ctx = canvas.getContext('2d');
    
    if (!this.ctx) return;

    this.setupIntersectionObserver();
    
    // Draw initial frame and potentially start
    this.drawFrame((performance.now() / 1000) * this.getEffectiveSpeed());
    
    if (!this.io) {
      this.start();
    }
    
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.ctx) {
      // Re-setup canvas size if size changed
      if (changes['size']) {
        const canvas = this.canvasRef.nativeElement;
        this.dpr = Math.min(1, 24 / this.size);
        canvas.width = Math.round(this.size * this.dpr);
        canvas.height = Math.round(this.size * this.dpr);
      }
      
      // If we are paused, just draw one frame
      if (changes['paused'] || changes['state'] || changes['size'] || changes['theme'] || changes['speed']) {
        if (!this.running) {
          this.drawFrame((performance.now() / 1000) * this.getEffectiveSpeed());
        } else if (this.paused) {
          this.stop();
        } else if (!this.paused && this.visible && document.visibilityState !== 'hidden') {
          this.start();
        }
      }
    }
  }

  ngOnDestroy() {
    this.stop();
    if (this.io) {
      this.io.disconnect();
    }
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private getEffectiveSpeed(): number {
    const { speed: baseSpeed } = resolvePreset(this.state, this.size as OrbSize);
    return baseSpeed * this.speed;
  }

  private drawFrame = (tSec: number) => {
    if (!this.ctx) return;
    
    const dark = getResolvedDark(this.theme, this.canvasRef.nativeElement);
    const { mode, opts } = resolvePreset(this.state, this.size as OrbSize);
    const draw = MODE_DRAWS[mode];

    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.clearRect(0, 0, this.size, this.size);
    draw(this.ctx, this.size, tSec, dark, opts);
  };

  private loop = () => {
    this.drawFrame((performance.now() / 1000) * this.getEffectiveSpeed());
    if (this.running) {
      this.rafId = requestAnimationFrame(this.loop);
    }
  };

  private start() {
    if (this.running || this.paused || getReducedMotion()) {
      if (getReducedMotion() && !this.running) {
        // Draw static frame for reduced motion
        this.drawFrame(0.6);
      }
      return;
    }
    this.running = true;
    this.ngZone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame(this.loop);
    });
  }

  private stop() {
    this.running = false;
    if (typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.rafId);
    }
  }

  private setupIntersectionObserver() {
    if (typeof IntersectionObserver !== 'undefined') {
      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible && document.visibilityState !== 'hidden') {
          this.start();
        } else {
          this.stop();
        }
      });
      this.io.observe(this.canvasRef.nativeElement);
    }
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.stop();
    } else if (this.visible) {
      this.start();
    }
  };
}
